import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { ExerciseInterface } from "../../interfaces/examInterfaces";
import { CircularProgress, styled, Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { COLORS } from "../../constants";
import { RootState } from "../..";
import { cloneDeep } from "lodash";
import { addExercise, removeExercise } from "./Functions/FunctionsManipulateExercises";
import { getImageUrls } from "./Functions/FunctionsCreatePages";
import { useLocation } from "react-router-dom";

const TableCellImage = styled(TableCell)(({ theme }) => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  padding: "0 0 0 4px",
  position: "relative",

  "&:hover": {
    transition: "filter 0.15s ease-in-out",
    filter: "brightness(0.9)",
  },
  "&:hover .sticker-new": {
    opacity: 0.4,
  },
}));

const PointsBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  right: "12px",
  bottom: "12px",
}));

const ImageBox = styled(Box)(({ theme }) => ({
  backgroundColor: "white",
  padding: "12px",
  width: "100%",
  display: "flex",
  justifyContent: "center",

  "& img": {
    maxWidth: "700px",
    width: "100%",
  },
}));

const StickerNew = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  right: 0,
  backgroundColor: COLORS.DIDACTO_BLUE,
  borderRadius: "0 0 0 1.6rem",
  fontsize: "1.8rem",
  color: "white",
  fontFamily: "TisaSansPro-Bold",
  padding: "4px 15px",
}));

const useStyles = makeStyles((theme: Theme) => ({
  tableRowRoot: {
    "&$tableRowSelected, &$tableRowSelected:hover": {
      backgroundColor: COLORS.DIDACTO_BLUE,
    },
  },
  tableRowSelected: {
    backgroundColor: "blue",
  },
}));

export const SelectingExercisesTable = ({
  exercises,
  index,
  sortedExercises,
}: {
  exercises: ExerciseInterface[];
  index: number;
  sortedExercises: ExerciseInterface[];
}) => {
  const [selectedExercises, setSelectedExercises] = useState<number[]>([]);
  const labelId = `enhanced-table-checkbox-${index}`;
  const [copySelectedExercises, setCopySelectedExercises] = useState<number[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const imageUrls = useSelector((state: RootState) => state.examBuilder.main.imageUrls || {});
  const solutionImageUrls = useSelector((state: RootState) => state.examBuilder.main.solutionImageUrls || {});
  const newExercises = useSelector((state: RootState) => state.examBuilder.manipulateExercises.newExercises);
  const statusOpenDialog = useSelector((state: RootState) => state.dialogs.selectExercisesExamDialog);
  const classes = useStyles();

  const { pathname } = useLocation();
  const isDemoMode = pathname.startsWith("/demo");

  useEffect(() => {
    if (statusOpenDialog) {
      const ids = sortedExercises.map((exercise) => exercise.id);
      const numericExerciseIds = ids.map((id: string) => +id);
      setSelectedExercises(numericExerciseIds);

      const copySelectedExercises = cloneDeep(numericExerciseIds);
      setCopySelectedExercises(copySelectedExercises);
    }
  }, [statusOpenDialog]);

  const handleClickExercise = (id: number) => {
    const selectedIndex = selectedExercises.indexOf(id);
    let newSelected = setSelectedRow(selectedIndex, selectedExercises, id);
    if (selectedIndex !== -1) {
      removeExercise(id.toString(), newExercises);
    } else {
      const newExercise = exercises.find((exercise) => exercise.id === id.toString());
      if (newExercise) {
        addExercise(newExercise, newExercises);
      }
    }
    setSelectedExercises(newSelected);
  };

  useEffect(() => {
    getImageUrls(exercises, imageUrls, solutionImageUrls, isDemoMode);
  }, [exercises]);

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <TableContainer>
          <Table aria-labelledby="tableTitle">
            <TableBody>
              {exercises.filter((exercise) => !copySelectedExercises.includes(+exercise.id)).length === 0 ? (
                <TableRow>
                  <TableCell>Du hast bereits alle Aufgaben dieses Niveaus eingefügt.</TableCell>
                </TableRow>
              ) : (
                exercises
                  .filter((exercise) => !copySelectedExercises.includes(+exercise.id))
                  .reverse()
                  .map((exercise) => {
                    const imageUrl = imageUrls[exercise.id];
                    const isExerciseSelected = selectedExercises.includes(+exercise.id);
                    const showStickerNew = Math.floor(Date.now() / 1000) < exercise.newUntil;

                    return (
                      <TableRow
                        hover
                        onClick={() => handleClickExercise(+exercise.id)}
                        role="checkbox"
                        aria-checked={false}
                        selected={isExerciseSelected}
                        tabIndex={-1}
                        key={exercise.id}
                        sx={{ cursor: "pointer" }}
                        classes={{
                          root: classes.tableRowRoot,
                          selected: classes.tableRowSelected,
                        }}
                      >
                        <TableCellImage component="th" id={labelId} scope="row" padding="none">
                          <ImageBox>
                            {showStickerNew && <StickerNew className="sticker-new">Neu</StickerNew>}
                            {isLoading && (
                              <CircularProgress
                                sx={{
                                  position: "absolute",
                                  top: 0,
                                  left: 0,
                                  zIndex: 1,
                                }}
                              />
                            )}
                            {imageUrl !== null && <img src={imageUrl} alt="Bild" onLoad={() => setIsLoading(false)} />}
                          </ImageBox>

                          <PointsBox>{exercise.points} P.</PointsBox>
                        </TableCellImage>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

function setSelectedRow(selectedIndex: number, selected: number[], id: number) {
  let newSelected: number[] = [];
  if (selectedIndex === -1) {
    newSelected = newSelected.concat(selected, id);
  } else if (selectedIndex === 0) {
    newSelected = newSelected.concat(selected.slice(1));
  } else if (selectedIndex === selected.length - 1) {
    newSelected = newSelected.concat(selected.slice(0, -1));
  } else if (selectedIndex > 0) {
    newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
  }

  return newSelected;
}
